"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Check,
  CircleHelp,
  CreditCard,
  HardDrive,
  Monitor,
  RefreshCw,
  Server,
  Sparkles,
} from "lucide-react";
import { useLanguage } from "@/components/LanguageProvider";
import { formatCompactTokenCount } from "@/components/settings/UsageDashboard";
import { unwrapApiData } from "@/lib/apiResponse";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { useAppResumeRevision } from "@/lib/useAppResumeRevision";
import { pickI18n } from "@/lib/planI18n";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
const BYTES_PER_GB = 1024 ** 3;
const BYTES_PER_TB = 1024 ** 4;

type PlanEntitlements = {
  aiTokensPerMonth?: number | null;
  storageBytes?: number | null;
  paasEnabled?: boolean;
  maxDevices?: number | null;
  aiOpinionEnabled?: boolean;
  dataExportEnabled?: boolean;
  emailSupportEnabled?: boolean;
  prioritySupportEnabled?: boolean;
  dedicatedSupportEnabled?: boolean;
  customDevelopmentEnabled?: boolean;
};

type ResolvedPlanResponse = {
  plan: {
    planId?: number;
    planName?: string;
    nameI18n?: Record<string, string> | null;
    planDescription?: string | null;
    descriptionI18n?: Record<string, string> | null;
  } | null;
  subscription: {
    status?: string;
    billingPeriod?: "MONTHLY" | "YEARLY";
    campaignId?: number | null;
    currentPeriodEnd?: string | null;
    cancelAtPeriodEnd?: boolean;
    scheduledPlanVersionId?: number | null;
    scheduledChangeAt?: string | null;
    scheduledChangeReason?: string | null;
  } | null;
  baseEntitlements?: PlanEntitlements;
  entitlements: PlanEntitlements;
  activeGrants?: Array<{
    grantId: number;
    source: string;
    endsAt: string;
    reason: string;
  }>;
  overrides?: Array<{
    overrideId: number;
    name?: string;
    reason?: string;
  }>;
  appliedPromotions?: Array<{
    campaignId: number;
    name: string;
    reason?: string | null;
  }>;
  planVersionId?: number | null;
};

type PricingService = {
  serviceId: number;
  serviceKey?: string | null;
  label?: string;
  serviceLabel?: string;
  description?: string | null;
  value: string | boolean;
};

type PricingServiceGroup = {
  groupId: number;
  name?: string;
  groupName?: string;
  services: PricingService[];
};

type PublicPlan = {
  id: string;
  planId?: number;
  name?: string;
  planName?: string;
  name_i18n?: Record<string, string> | null;
  description?: string | null;
  planDescription?: string | null;
  description_i18n?: Record<string, string> | null;
  billingAvailable?: boolean;
  service_groups?: PricingServiceGroup[];
  serviceGroups?: PricingServiceGroup[];
};

type PlanDashboardData = {
  resolved: ResolvedPlanResponse;
  publicPlan: PublicPlan | null;
};

function finiteNumber(value: number | null | undefined): number | null {
  if (value == null) return value === null ? null : Number.NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : Number.NaN;
}

function formatStorageLimit(
  value: number | null | undefined,
  unlimitedLabel: string,
): string {
  const bytes = finiteNumber(value);
  if (bytes === null) return unlimitedLabel;
  if (!Number.isFinite(bytes)) return "—";
  if (bytes >= BYTES_PER_TB) {
    return `${(bytes / BYTES_PER_TB).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })} TB`;
  }
  return `${(bytes / BYTES_PER_GB).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })} GB`;
}

function formatNumberLimit(
  value: number | null | undefined,
  unlimitedLabel: string,
  suffix: string,
): string {
  const count = finiteNumber(value);
  if (count === null) return unlimitedLabel;
  if (!Number.isFinite(count)) return "—";
  return `${count.toLocaleString()}${suffix}`;
}

function formatTokenLimit(
  value: number | null | undefined,
  unlimitedLabel: string,
  suffix: string,
): string {
  const tokens = finiteNumber(value);
  if (tokens === null) return unlimitedLabel;
  if (!Number.isFinite(tokens)) return "—";
  return `${formatCompactTokenCount(Number(tokens))}${suffix}`;
}

function originalValueWhenChanged(
  originalValue: string,
  finalValue: string,
  hasAdjustment: boolean,
): string | undefined {
  return hasAdjustment && originalValue !== "—" && originalValue !== finalValue
    ? originalValue
    : undefined;
}

export function PlanDashboard({ onViewPlans }: { onViewPlans: () => void }) {
  const { t, language } = useLanguage();
  const resumeRevision = useAppResumeRevision();
  const [data, setData] = useState<PlanDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadPlan() {
      setLoading(true);
      setLoadError(false);
      try {
        const [entitlementsResponse, plansResponse] = await Promise.all([
          fetchWithAuth(`${API_URL}/api/pricing/entitlements/me?scope=user`, {
            signal: controller.signal,
          }),
          fetchWithAuth(`${API_URL}/api/subscriptions/plans`, {
            signal: controller.signal,
          }),
        ]);
        if (!entitlementsResponse.ok) {
          throw new Error(
            `Plan request failed: ${entitlementsResponse.status}`,
          );
        }

        const resolved = unwrapApiData<ResolvedPlanResponse>(
          await entitlementsResponse.json(),
        );
        if (!resolved?.entitlements) {
          throw new Error("Plan response is invalid");
        }

        let publicPlan: PublicPlan | null = null;
        if (plansResponse.ok) {
          const catalog = unwrapApiData<{ plans?: PublicPlan[] }>(
            await plansResponse.json(),
          );
          const currentPlanId = resolved.plan?.planId;
          publicPlan =
            catalog?.plans?.find(
              (plan) => Number(plan.planId ?? plan.id) === currentPlanId,
            ) ?? null;
        }

        if (!controller.signal.aborted) {
          setData({ resolved, publicPlan });
        }
      } catch {
        if (!controller.signal.aborted) setLoadError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void loadPlan();
    return () => controller.abort();
  }, [resumeRevision, retryKey]);

  if (loading && !data) return <PlanSkeleton />;

  if (loadError && !data) {
    return (
      <div
        role="alert"
        className="flex items-start justify-between gap-4 rounded-xl bg-gray-100/80 p-4 dark:bg-white/[0.06]"
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-gray-500 shadow-sm dark:bg-[#2a2a2d] dark:text-gray-400">
            <CreditCard size={17} strokeWidth={1.8} />
          </span>
          <div>
            <p className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100">
              {t("settings.plan_load_error")}
            </p>
            <p className="mt-1 text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
              {t("settings.load_error")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setRetryKey((value) => value + 1)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[10.5px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-[#343439] dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5"
        >
          <RefreshCw size={13} />
          {t("settings.retry")}
        </button>
      </div>
    );
  }

  const currentPlan = data?.resolved.plan ?? null;
  if (!data || !currentPlan) {
    return (
      <div className="rounded-xl bg-gray-100/80 p-5 text-center dark:bg-white/[0.06]">
        <p className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100">
          {t("settings.plan_empty")}
        </p>
        <button
          type="button"
          onClick={onViewPlans}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gray-950 px-3.5 py-2 text-[11px] font-semibold text-white transition-colors hover:bg-black dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
        >
          {t("settings.plan_view_plans")}
          <ArrowRight size={13} />
        </button>
      </div>
    );
  }

  const { resolved, publicPlan } = data;
  const { entitlements, subscription } = resolved;
  const planName = pickI18n(
    publicPlan?.name_i18n ?? currentPlan.nameI18n,
    language,
    publicPlan?.name ?? publicPlan?.planName ?? currentPlan.planName,
  );
  const planDescription = pickI18n(
    publicPlan?.description_i18n ?? currentPlan.descriptionI18n,
    language,
    publicPlan?.description ??
      publicPlan?.planDescription ??
      currentPlan.planDescription,
  );
  const groups = publicPlan?.service_groups ?? publicPlan?.serviceGroups ?? [];
  const periodEnd = subscription?.currentPeriodEnd
    ? formatDate(subscription.currentPeriodEnd, language)
    : null;
  const periodMessage =
    periodEnd && subscription?.cancelAtPeriodEnd
      ? t("settings.plan_ends_on").replace("{{date}}", periodEnd)
      : periodEnd
        ? t("settings.plan_renews_on").replace("{{date}}", periodEnd)
        : null;
  const hasPromotionApplied =
    subscription?.campaignId != null ||
    resolved.activeGrants?.some((grant) => grant.source === "PROMOTION");
  const appliedPromotions = resolved.appliedPromotions ?? [];
  const hasLimitAdjustment =
    hasPromotionApplied ||
    appliedPromotions.length > 0 ||
    (resolved.overrides?.length ?? 0) > 0;
  const baseEntitlements = resolved.baseEntitlements;

  const tokenLimit = formatTokenLimit(
    entitlements.aiTokensPerMonth,
    t("settings.plan_unlimited"),
    t("settings.plan_token_suffix"),
  );
  const baseTokenLimit = formatTokenLimit(
    baseEntitlements?.aiTokensPerMonth,
    t("settings.plan_unlimited"),
    t("settings.plan_token_suffix"),
  );
  const storageLimit = formatStorageLimit(
    entitlements.storageBytes,
    t("settings.plan_unlimited"),
  );
  const baseStorageLimit = formatStorageLimit(
    baseEntitlements?.storageBytes,
    t("settings.plan_unlimited"),
  );
  const platformLimit = entitlements.paasEnabled
    ? t("settings.plan_included")
    : t("settings.plan_not_included");
  const basePlatformLimit =
    baseEntitlements?.paasEnabled === undefined
      ? "—"
      : baseEntitlements.paasEnabled
        ? t("settings.plan_included")
        : t("settings.plan_not_included");
  const deviceLimit = formatNumberLimit(
    entitlements.maxDevices,
    t("settings.plan_unlimited"),
    t("settings.plan_device_suffix"),
  );
  const baseDeviceLimit = formatNumberLimit(
    baseEntitlements?.maxDevices,
    t("settings.plan_unlimited"),
    t("settings.plan_device_suffix"),
  );

  const resolveServiceValue = (service: PricingService): string | boolean => {
    switch (service.serviceKey) {
      case "AI_TOKENS":
        return tokenLimit;
      case "STORAGE":
        return formatStorageLimit(
          entitlements.storageBytes,
          t("settings.plan_unlimited"),
        );
      case "PAAS":
        return Boolean(entitlements.paasEnabled);
      case "MAX_DEVICES":
        return formatNumberLimit(
          entitlements.maxDevices,
          t("settings.plan_unlimited"),
          t("settings.plan_device_suffix"),
        );
      case "AI_OPINION":
        return Boolean(entitlements.aiOpinionEnabled);
      case "DATA_EXPORT":
        return Boolean(entitlements.dataExportEnabled);
      case "EMAIL_SUPPORT":
        return Boolean(entitlements.emailSupportEnabled);
      case "PRIORITY_SUPPORT":
        return Boolean(entitlements.prioritySupportEnabled);
      case "DEDICATED_SUPPORT":
        return Boolean(entitlements.dedicatedSupportEnabled);
      case "CUSTOM_DEVELOPMENT":
        return Boolean(entitlements.customDevelopmentEnabled);
      default:
        return service.value;
    }
  };

  return (
    <div>
      <section className="rounded-xl bg-gray-100/80 p-4 dark:bg-white/[0.06]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-gray-600 shadow-sm dark:bg-[#2a2a2d] dark:text-gray-300">
              <CreditCard size={18} strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
              <p className="text-[10.5px] font-medium text-gray-500 dark:text-gray-400">
                {t("settings.plan_current")}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h3 className="text-[16px] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
                  {planName || currentPlan.planName || "—"}
                </h3>
                <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
                  {subscription
                    ? t("settings.plan_active")
                    : t("settings.plan_default")}
                </span>
                {subscription?.billingPeriod && (
                  <span className="rounded-md bg-white px-2 py-0.5 text-[9px] font-semibold text-gray-500 dark:bg-white/10 dark:text-gray-400">
                    {subscription.billingPeriod === "YEARLY"
                      ? t("settings.plan_yearly")
                      : t("settings.plan_monthly")}
                  </span>
                )}
                {appliedPromotions.map((promotion) => (
                  <PlanAdjustmentBadge
                    key={`promotion-${promotion.campaignId}`}
                    label={promotion.name}
                    reason={
                      promotion.reason?.trim() ||
                      t("settings.plan_promotion_reason_fallback")
                    }
                    tone="promotion"
                  />
                ))}
                {appliedPromotions.length === 0 && hasPromotionApplied && (
                  <PlanAdjustmentBadge
                    label={t("settings.plan_promotion_applied")}
                    reason={t("settings.plan_promotion_reason_fallback")}
                    tone="promotion"
                  />
                )}
                {(resolved.overrides ?? []).map((override) => (
                  <PlanAdjustmentBadge
                    key={`override-${override.overrideId}`}
                    label={
                      override.name?.trim() ||
                      t("settings.plan_admin_override_applied")
                    }
                    reason={
                      override.reason?.trim() ||
                      t("settings.plan_override_reason_fallback")
                    }
                    tone="override"
                  />
                ))}
                {subscription?.cancelAtPeriodEnd && (
                  <span className="rounded-md bg-rose-50 px-2 py-0.5 text-[9px] font-semibold text-rose-600 dark:bg-rose-950/25 dark:text-rose-400">
                    {t("settings.plan_cancel_scheduled")}
                  </span>
                )}
                {subscription?.scheduledPlanVersionId && (
                  <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[9px] font-semibold text-amber-700 dark:bg-amber-950/25 dark:text-amber-400">
                    플랜 변경 예약
                  </span>
                )}
              </div>
              {planDescription && (
                <p className="mt-1.5 max-w-md text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
                  {planDescription}
                </p>
              )}
              {periodMessage && (
                <p className="mt-1 text-[10px] font-medium text-gray-400 dark:text-gray-500">
                  {periodMessage}
                </p>
              )}
              {(resolved.activeGrants?.length ?? 0) > 0 && (
                <p className="mt-1 text-[10px] font-medium text-seexr-primary">
                  기간 한정 추가 권한 {resolved.activeGrants!.length}건 적용 중
                </p>
              )}
              {publicPlan?.billingAvailable === false && (
                <p className="mt-1 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                  신규 유료 결제는 현재 준비 중입니다.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onViewPlans}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-gray-950 px-3.5 text-[11px] font-semibold text-white transition-colors hover:bg-black dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
          >
            {t("settings.plan_view_plans")}
            <ArrowRight size={13} />
          </button>
        </div>
      </section>

      <section className="mt-5">
        <h3 className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100">
          {t("settings.plan_allowances")}
        </h3>
        <div className="mt-2.5 grid grid-cols-2 gap-2.5">
          <PlanMetric
            icon={Sparkles}
            label={t("settings.plan_tokens")}
            value={tokenLimit}
            originalValue={originalValueWhenChanged(
              baseTokenLimit,
              tokenLimit,
              hasLimitAdjustment,
            )}
          />
          <PlanMetric
            icon={HardDrive}
            label={t("settings.plan_storage")}
            value={storageLimit}
            originalValue={originalValueWhenChanged(
              baseStorageLimit,
              storageLimit,
              hasLimitAdjustment,
            )}
          />
          <PlanMetric
            icon={Server}
            label={t("settings.plan_platform")}
            value={platformLimit}
            originalValue={originalValueWhenChanged(
              basePlatformLimit,
              platformLimit,
              hasLimitAdjustment,
            )}
          />
          <PlanMetric
            icon={Monitor}
            label={t("settings.plan_devices")}
            value={deviceLimit}
            originalValue={originalValueWhenChanged(
              baseDeviceLimit,
              deviceLimit,
              hasLimitAdjustment,
            )}
          />
        </div>
      </section>

      {groups.length > 0 && (
        <section className="mt-5">
          <h3 className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100">
            {t("settings.plan_details")}
          </h3>
          <div className="mt-2.5 overflow-hidden rounded-xl border border-gray-200 dark:border-[#303034]">
            {groups.map((group, groupIndex) => (
              <div
                key={group.groupId}
                className={
                  groupIndex > 0
                    ? "border-t border-gray-200 dark:border-[#303034]"
                    : undefined
                }
              >
                <div className="bg-gray-50 px-3.5 py-2 dark:bg-white/[0.035]">
                  <p className="text-[10.5px] font-semibold text-gray-700 dark:text-gray-300">
                    {group.name ?? group.groupName}
                  </p>
                </div>
                <dl className="divide-y divide-gray-100 px-3.5 dark:divide-[#29292c]">
                  {group.services
                    .filter((service) => service.serviceKey !== "API_ACCESS")
                    .map((service) => (
                      <PlanServiceRow
                        key={service.serviceId}
                        label={service.label ?? service.serviceLabel ?? "—"}
                        description={service.description}
                        value={resolveServiceValue(service)}
                        includedLabel={t("settings.plan_included")}
                        excludedLabel={t("settings.plan_not_included")}
                      />
                    ))}
                </dl>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PlanMetric({
  icon: Icon,
  label,
  value,
  originalValue,
}: {
  icon: typeof Sparkles;
  label: string;
  value: string;
  originalValue?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 px-3 py-3 dark:border-[#303034]">
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Icon size={14} strokeWidth={1.8} />
        <p className="text-[10px] font-medium">{label}</p>
      </div>
      <p className="mt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 tabular-nums">
        {originalValue && (
          <del className="text-[10.5px] font-medium text-gray-400 decoration-gray-400/80 dark:text-gray-500 dark:decoration-gray-500/80">
            {originalValue}
          </del>
        )}
        <span
          className={`text-[13px] font-semibold ${
            originalValue
              ? "text-seexr-primary dark:text-seexr-400"
              : "text-gray-950 dark:text-gray-50"
          }`}
        >
          {value}
        </span>
      </p>
    </div>
  );
}

function PlanAdjustmentBadge({
  label,
  reason,
  tone,
}: {
  label: string;
  reason: string;
  tone: "promotion" | "override";
}) {
  const { t } = useLanguage();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const openTooltip = useCallback(() => {
    if (triggerRef.current) {
      setAnchorRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);
  const closeTooltip = useCallback(() => setAnchorRect(null), []);

  useEffect(() => {
    if (!anchorRect) return;
    const updatePosition = () => {
      if (triggerRef.current) {
        setAnchorRect(triggerRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRect]);

  const toneClass =
    tone === "promotion"
      ? "bg-seexr-50 text-seexr-primary dark:bg-seexr-400/10 dark:text-seexr-400"
      : "bg-amber-50 text-amber-700 dark:bg-amber-950/25 dark:text-amber-400";

  return (
    <span
      className={`inline-flex items-center rounded-md py-0.5 pl-2 pr-1 text-[9px] font-semibold ${toneClass}`}
    >
      {label}
      <span
        ref={triggerRef}
        aria-label={`${label} ${t("settings.plan_adjustment_reason")}`}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full opacity-70 transition hover:opacity-100"
      >
        <CircleHelp size={11} strokeWidth={2} />
      </span>
      {anchorRect &&
        typeof document !== "undefined" &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[2147483647] w-60 max-w-[calc(100vw-24px)] whitespace-normal rounded-lg border border-gray-200 bg-white px-3 py-2 text-left font-normal text-gray-600 opacity-100 shadow-[0_8px_24px_rgba(15,23,42,0.16)] dark:border-[#3a3a3e] dark:bg-[#242427] dark:text-gray-300"
            style={{
              left: Math.min(
                Math.max(anchorRect.left + anchorRect.width / 2, 132),
                window.innerWidth - 132,
              ),
              top:
                anchorRect.top < window.innerHeight / 2
                  ? anchorRect.bottom + 6
                  : anchorRect.top - 6,
              transform:
                anchorRect.top < window.innerHeight / 2
                  ? "translateX(-50%)"
                  : "translate(-50%, -100%)",
            }}
          >
            <span className="block text-[9px] font-semibold text-gray-400 dark:text-gray-500">
              {t("settings.plan_adjustment_reason")}
            </span>
            <span className="mt-1 block text-[10px] leading-relaxed">
              {reason}
            </span>
          </span>,
          document.body,
        )}
    </span>
  );
}

function PlanServiceRow({
  label,
  description,
  value,
  includedLabel,
  excludedLabel,
}: {
  label: string;
  description?: string | null;
  value: string | boolean;
  includedLabel: string;
  excludedLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <dt className="text-[10.5px] font-medium text-gray-700 dark:text-gray-300">
          {label}
        </dt>
        {description && (
          <p className="mt-0.5 text-[9.5px] leading-relaxed text-gray-400 dark:text-gray-500">
            {description}
          </p>
        )}
      </div>
      <dd className="shrink-0 text-right text-[10.5px] font-semibold text-gray-900 dark:text-gray-100">
        {typeof value === "boolean" ? (
          value ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <Check size={13} strokeWidth={2.2} />
              {includedLabel}
            </span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">
              {excludedLabel}
            </span>
          )
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function PlanSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-[104px] rounded-xl bg-gray-100 dark:bg-white/[0.06]" />
      <div className="mt-5 h-4 w-20 rounded bg-gray-100 dark:bg-white/[0.06]" />
      <div className="mt-2.5 grid grid-cols-2 gap-2.5">
        {[0, 1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-[72px] rounded-xl bg-gray-100 dark:bg-white/[0.06]"
          />
        ))}
      </div>
    </div>
  );
}

function formatDate(value: string, language: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}
